import React from 'react';
import {Formik, Form} from 'formik';
import formFieldMap from './FormFieldMaps';
import Wrapper from 'Components/Wrapper';
import SectionCardTitle from 'Components/SectionCardTitle';
import {Card, Flex, Row} from 'antd';

// Field types whose `required` flag is enforced (buttons/checkboxes excluded).
const REQUIRED_CHECK_TYPES = [
  'text',
  'textarea',
  'select',
  'datePicker',
  'dateTimePicker',
  'commentBox',
];

const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

/**
 * Enforces each field config's `required` flag for every form built on this
 * wrapper. The old MUI fields got this from the browser's native `required`
 * check; antd inputs don't, so fields required only by config (and ones code
 * toggles at runtime, e.g. confirmedSRFieldsRequired / handleMailingAddressClick)
 * could be saved empty. Reads the current section configs on every validation,
 * so conditional requirements always follow the code. Hidden, disabled and
 * read-only fields are skipped, and "Reset" never needs a complete form.
 */
const validateRequiredFields = (values, sections) => {
  const errors: Record<string, string> = {};
  if (values?.buttonAction === 'Reset') return errors;
  sections.forEach((section) =>
    section?.fields?.forEach((field) => {
      if (
        field?.required &&
        !field.isHidden &&
        !field.isDisabled &&
        !field.readonly && // browsers skip readonly inputs in native validation too
        field.display !== 'none' &&
        REQUIRED_CHECK_TYPES.includes(field.fieldType) &&
        isEmptyValue(values?.[field.name])
      ) {
        errors[field.name] = `${(field.fieldLabel || field.name).trim()} is required`;
      }
    }),
  );
  return errors;
};

const FormikForm = (props) => {
  let customProps = props;
  return (
    <Formik
      initialValues={customProps.initialValues}
      validationSchema={customProps.validationSchema}
      // Formik merges these errors with the validationSchema errors.
      validate={(values) =>
        validateRequiredFields(values, [customProps.section1, customProps.section2])
      }
      onSubmit={(values, actions) => {
        // Several SR forms' submit handlers return undefined rather than a
        // promise; wrap so `.then` never throws and a failed submit always
        // releases isSubmitting while keeping the entered values in place.
        return Promise.resolve()
          .then(() => customProps.onSubmit(values, actions))
          .catch((error) => console.error('Form submit failed:', error))
          .finally(() => actions.setSubmitting(false));
      }}
      enableReinitialize={true}>
      {(props) => (
        <Form>
          <Wrapper>
            <Card style={{marginTop: 16, marginBottom: 16}}>
              {customProps.topButtonStack ? (
                <Row gutter={16}>
                  {customProps.topButtonStack.fields.map((field) => {
                    return formFieldMap[field.fieldType](field, props, customProps);
                  })}
                </Row>
              ) : null}
              {customProps.section1 ? (
                <Flex vertical gap={16} style={{padding: 32}}>
                  <SectionCardTitle>{customProps.section1.title}</SectionCardTitle>
                  <Row gutter={16}>
                    {customProps.section1.fields.map((field) => {
                      return formFieldMap[field.fieldType](field, props, customProps);
                    })}
                  </Row>
                </Flex>
              ) : null}
              {customProps.section2 ? (
                <Flex vertical gap={16} style={{padding: 32}}>
                  <SectionCardTitle>{customProps.section2.title}</SectionCardTitle>
                  <Row gutter={16}>
                    {customProps.section2.fields.map((field) => {
                      return formFieldMap[field.fieldType](field, props, customProps);
                    })}
                  </Row>
                </Flex>
              ) : null}
              {/* need to copy at top */}
              {customProps.buttonStack ? (
                <Row gutter={16} justify="end">
                  {customProps.buttonStack.fields.map((field) => {
                    return formFieldMap[field.fieldType](field, props, customProps);
                  })}
                </Row>
              ) : null}
            </Card>
          </Wrapper>
        </Form>
      )}
    </Formik>
  );
};

export default FormikForm;
